import { Form, InputGroup } from '@valid-eval/shared-react-components';
import cx from 'classnames';
import JoditEditor from 'jodit-react';
import { useMemo } from 'react';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';

import { required as requiredVal } from 'components/FormFields/validations';
import { isRunningOnIE } from 'utils';

import Styles from './RichText.module.scss';

export type RichTextProps = {
  id?: string;
  label?: string;
  name: string;
  placeholder?: string;
  rules?: Omit<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  transform?(value: string): string;
  disabled?: boolean;
  required?: boolean;
  helpText?: string;
  autoFocus?: boolean;
};

const controls = [
  'bold',
  'strikethrough',
  'underline',
  'italic',
  'ul',
  'ol',
  ...(isRunningOnIE() ? [] : ['link']),
  '|',
  'undo',
  'redo',
];

const RichText = ({
  id,
  label,
  name,
  placeholder,
  rules,
  transform = (value: string) => value,
  disabled,
  required,
  helpText,
  autoFocus,
}: RichTextProps) => {
  const { control, formState } = useFormContext();

  const showValidation =
    !!formState?.touchedFields?.[name] || !!formState?.dirtyFields?.[name] || formState.isSubmitted;

  const isInvalid = !!formState?.errors?.[name] && showValidation;
  const isValid = !formState?.errors?.[name] && showValidation;

  rules = rules || {};
  rules = required ? { ...rules, validate: { required: requiredVal, ...rules.validate } } : rules;

  const configMemoized = useMemo(
    () => ({
      className: cx({
        [Styles.Invalid]: isInvalid,
        [Styles.Valid]: isValid,
      }),
      defaultActionOnPaste: 'insert_clear_html',
      showPlaceholder: !!placeholder,
      placeholder: placeholder,
      readonly: false,
      spellcheck: true,
      askBeforePasteHTML: false,
      processPasteHTML: false,
      addNewLine: false,
      buttons: controls,
      buttonsMD: controls,
      buttonsSM: controls,
      buttonsXS: controls,
      link: {
        noFollowCheckbox: false,
        openInNewTabCheckbox: false,
      },
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      autofocus: autoFocus,
    }),
    [isInvalid, isValid],
  );

  return (
    <>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, value, ref: fieldRef } }) => {
          return (
            <Form.Group className="mb-2">
              {label && (
                <Form.Label className="w-100" htmlFor={id || name}>
                  {label}
                </Form.Label>
              )}
              {helpText && (
                <p className="d-print-none text-pre-wrap">
                  <Form.Text muted>{helpText}</Form.Text>
                </p>
              )}
              <InputGroup className="d-block" hasValidation>
                <JoditEditor
                  // @ts-expect-error InsertMode is not typed
                  config={configMemoized}
                  ref={fieldRef}
                  value={value}
                  onBlur={(value) => onChange(transform(value))}
                  disabled={disabled}
                />
                {isInvalid && (
                  <Form.Control.Feedback type="invalid" className={isInvalid ? 'd-block' : ''}>
                    {String(formState.errors[name]?.message)}
                  </Form.Control.Feedback>
                )}
              </InputGroup>
            </Form.Group>
          );
        }}
      />
    </>
  );
};

export default RichText;
